import { useEffect, useState } from "react";
import { Mail, Loader2, X } from "lucide-react";
import { api, useAuth } from "../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

/* EmailVerificationBanner
 * -----------------------
 * Shown whenever a logged-in user's email is not yet verified. Always visible
 * (not dismissible) to enforce the "no full access until email is verified"
 * policy. Clicking "Verificar" opens a modal where the user can paste the
 * 6-digit code or request a fresh one (30 s cooldown enforced server-side).
 */
export default function EmailVerificationBanner() {
  const { user, checkAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Tick the cooldown timer once a second so users see when they can resend.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Hide entirely when not logged in or when email is already verified.
  if (!user) return null;
  if (user.email_verified) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await api.post("/auth/email/send-code");
      toast.success("Te enviamos un código a tu correo");
      setCooldown(30);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429 && typeof detail === "string") {
        const m = detail.match(/(\d+)/);
        if (m) setCooldown(parseInt(m[1], 10));
      }
      toast.error(detail || "No pudimos enviar el código");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error("Ingresa los 6 dígitos");
      return;
    }
    setVerifying(true);
    try {
      await api.post("/auth/email/verify-code", { code });
      toast.success("¡Correo verificado!");
      await checkAuth();
      setOpen(false);
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código incorrecto");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <div
        data-testid="email-verification-banner"
        className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-amber-900 text-sm flex items-center justify-center gap-3 flex-wrap"
      >
        <Mail className="w-4 h-4 shrink-0" />
        <span className="text-center">
          Verifica tu correo <strong className="font-semibold">{user.email}</strong> para acceder a todas las funciones.
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-3 text-xs border-amber-700/30 text-amber-900 hover:bg-amber-100"
          onClick={() => setOpen(true)}
          data-testid="email-verification-banner-cta"
        >
          Verificar ahora
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verifica tu correo</DialogTitle>
            <DialogDescription>
              Ingresa el código de 6 dígitos que enviamos a{" "}
              <strong>{user.email}</strong>. El código expira en 10 minutos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <Input
              data-testid="email-verification-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              className="text-center text-2xl tracking-[0.5em] font-mono"
            />

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleResend}
                disabled={sending || cooldown > 0}
                data-testid="email-verification-resend-btn"
                className="text-sm text-terracotta hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {sending ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…
                  </span>
                ) : cooldown > 0 ? (
                  `Reenviar en ${cooldown}s`
                ) : (
                  "Reenviar código"
                )}
              </button>
              <span className="text-xs text-neutral-500">
                ¿No te llega? Revisa spam.
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              data-testid="email-verification-cancel-btn"
            >
              Cerrar
            </Button>
            <Button
              onClick={handleVerify}
              disabled={code.length !== 6 || verifying}
              data-testid="email-verification-submit-btn"
              className="bg-terracotta hover:bg-terracotta/90"
            >
              {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Verificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
